import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { eFeatureCenter } from '@models/classes/enums';
import { MoveVerticesFeature } from '@models/classes/features/move-vertices-feature';
import { Token } from '@models/classes/token';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { FeatureRenderer } from './feature';

export class MoveVerticesRenderer extends FeatureRenderer<MoveVerticesFeature> {
    modelToken = Token.MoveVerticesFeature;
    updateMeshGeometry(
        feature: MoveVerticesFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);
        const parentMesh = feature._parent;

        if (parentMesh && newVertexData.positions && newVertexData.positions.length > 0) {
            const newMeshVertexPositions = newVertexData.positions;
            if (feature.vertexIndices && feature.vertexIndices.length > 0) {
                let pivot: Vector3 | undefined;
                let rotationQuaternion: Quaternion | undefined;
                let scaleVector: Vector3 | undefined;

                if (feature.rotationAngle !== 0 && !feature.rotationAxis.equal({ x: 0, y: 0, z: 0 })) {
                    rotationQuaternion = Quaternion.RotationAxis(
                        feature.rotationAxis.toVec3(),
                        (feature.rotationAngle / 180) * Math.PI
                    );
                }
                if (!feature.scale.equal({ x: 1, y: 1, z: 1 })) {
                    scaleVector = feature.scale.toVec3();
                }

                // Calculate vertex center if needed
                if (rotationQuaternion || scaleVector) {
                    if (feature.center === eFeatureCenter.meshPivotPoint) {
                        pivot = parentMesh.pivot.toVec3();
                    } else {
                        // Calculate vertices barycenter
                        pivot = Vector3.Zero();
                        const currentMeshVertexPositions = newVertexData.positions;
                        if (currentMeshVertexPositions) {
                            feature.vertexIndices.forEach(idx => {
                                pivot!.x += currentMeshVertexPositions[idx * 3];
                                pivot!.y += currentMeshVertexPositions[idx * 3 + 1];
                                pivot!.z += currentMeshVertexPositions[idx * 3 + 2];
                            });

                            pivot.scaleInPlace(1 / feature.vertexIndices.length);
                        }
                    }
                }

                const vertexPosition = new Vector3();
                feature.vertexIndices.forEach(index => {
                    newMeshVertexPositions[index * 3] += feature.translation.x;
                    newMeshVertexPositions[index * 3 + 1] += feature.translation.y;
                    newMeshVertexPositions[index * 3 + 2] += feature.translation.z;

                    if (pivot && (rotationQuaternion || scaleVector)) {
                        vertexPosition.copyFromFloats(
                            newMeshVertexPositions[index * 3] - pivot.x,
                            newMeshVertexPositions[index * 3 + 1] - pivot.y,
                            newMeshVertexPositions[index * 3 + 2] - pivot.z
                        );

                        if (rotationQuaternion) {
                            vertexPosition.rotateByQuaternionToRef(rotationQuaternion, vertexPosition);
                        }

                        if (scaleVector) {
                            vertexPosition.multiplyInPlace(scaleVector);
                        }

                        vertexPosition.addInPlace(pivot);

                        newMeshVertexPositions[index * 3] = vertexPosition.x;
                        newMeshVertexPositions[index * 3 + 1] = vertexPosition.y;
                        newMeshVertexPositions[index * 3 + 2] = vertexPosition.z;
                    }
                });
            }
        }

        return newVertexData;
    }

    update3dInternal() {}
}
